<template>
    <div class="break-by-container">
        <div class="chart-type" v-if="!chartLoading">
            <label v-for="chartTypeOption in chartTypeOptions"
                   :key="chartTypeOption.icon"
                   v-tooltip.bottom="{ content: chartTypeOption.tooltip, delay: { show: 500, hide: 0 } }"
                   class="chart-type-icon-container">
                <input type="radio"
                       class="hidden-input"
                       v-model="chart"
                       :value="chartTypeOption.type">
                <IconBase class="chart-type-icon"
                          display="inline-flex"
                          :width="chartTypeOption.width"
                          :height="chartTypeOption.height"
                          :icon-name="chartTypeOption.icon"
                          :icon-hover-color="consts.COLORS.PRIMARY_500"
                          :icon-color="getChartOptionColor(chartTypeOption)"
                ></IconBase>
            </label>
        </div>
        <div class="break-by">
            <dropdown unique-id="radioDropdownLeaves"
                      :options="breakOptions"
                      optionsTitle="Break by "
                      class="flatStyle font-4 break-by-dropdown"
                      :selected-id="breakOption.id"
                      selected-display-text-field="titleText"
                      markSelected
                      @dropdownSelectedItemChanged="breakByChanged"
            >
            </dropdown>
            <div class="font-6">
                <span class="sort-by-text">Sort by: </span>
                <label :key="option"
                       class="sort-by-option"
                       v-for="option in sortOptions">
                    <input type="radio"
                           class="hidden-input"
                           v-model="sort"
                           :value="option">
                    <span>{{ option === 'name' ? 'ABC' : capitalize(option) }}</span>
                </label>
            </div>
        </div>
    </div>
</template>

<script>
import Dropdown from 'components/dropdowns/Dropdown.vue';
import IconBase from 'components/IconBase.vue';
import consts from 'services/metaData/consts';
import { capitalize } from 'services/utils/strings';
import chartTypes from 'components/charts/chartTypes';

const chartTypeOptions = [
    {
        icon: 'trendsLinesIcon',
        type: chartTypes.LineChart,
        tooltip: 'Trends (Lines)',
        width: 16,
        height: 10
    },
    {
        icon: 'trendsStackedIcon',
        type: chartTypes.ComboChart,
        tooltip: 'Trends (Stacked)',
        width: 16,
        height: 10
    },
    {
        icon: 'totalsBarsIcon',
        type: chartTypes.ColumnChart,
        tooltip: 'Totals (Bars)',
        width: 18,
        height: 18
    }
];

export default {
    name: 'BreakBy',
    components: {
        IconBase,
        Dropdown
    },
    data() {
        return {
            sort: this.sortBy,
            chart: this.chartType,
            consts
        };
    },
    watch: {
        sortBy(sortBy) {
            this.sort = sortBy;
        },
        sort(sortBy) {
            if (sortBy !== this.sortBy) {
                this.$emit('sortChanged', sortBy);
            }
        },
        chart(newChartType) {
            if (newChartType !== this.chartType) {
                this.$emit('chartTypeChanged', newChartType);
            }
        },
        chartType(newChartType) {
            this.chart = newChartType;
        }
    },
    props: {
        breakOptions: {
            type: Array,
            default: () => []
        },
        hiddenChartTypes: {
            type: Array,
            default: () => []
        },
        sortOptions: {
            type: Array,
            default: () => ['revenue', 'name']
        },
        breakBy: {
            type: String,
            required: true
        },
        sortBy: {
            type: String,
            required: true
        },
        chartLoading: Boolean,
        chartType: {
            type: String,
            default: chartTypes.LineChart
        }
    },
    computed: {
        chartTypeOptions() {
            return chartTypeOptions.filter((chartType) => !this.hiddenChartTypes.includes(chartType.type));
        },
        breakOption() {
            const defaultId = 3;
            const selectedOption = this.breakOptions.find((option) => option.key === this.breakBy);

            return selectedOption || this.breakOptions.find((option) => option.id === defaultId);
        }
    },
    methods: {
        capitalize,
        breakByChanged({ key }) {
            if (key === this.breakBy) return;
            this.$emit('breakChanged', key);
        },
        getChartOptionColor(option) {
            return option.type === this.chartType ? consts.COLORS.PRIMARY_500 : undefined;
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide";

.hidden-input {
    appearance: none;
    width: 0;
    height: 0;
    margin: 0;

    &:before {
       content: none;
    }
}

.break-by-container {
    display: flex;
    justify-content: flex-end;
}

.break-by {
    width: 213px;
    padding: 15px;
    border: $border;
    border-bottom: none;
}

.break-by-dropdown {
    text-decoration: none;
}

.break-by-dropdown.dropdown.flatStyle /deep/ .dropDownField {
    color: $dark-300;

    .truncate {
        text-decoration: none;
    }
}

.sort-by-text {
    color: $light-800;
}

.sort-by-option {
    margin-bottom: 3px;
    margin-left: 0;
    padding: 0 5px;
    cursor: pointer;

    input:checked + span {
        color: $primary-500;
    }

    &:not(:last-child) {
        border-right: $border;
    }
}

.chart-type {
    margin-right: 20px;
    margin-top: 15px
}

.chart-type-icon-container {
    display: inline-flex;

    input {
        display: none;
    }

    &:not(:last-child) {
        border-right: $border;
        height: 18px;
    }

    .chart-type-icon {
        padding: 3px 5px 0;
        cursor: pointer;
        align-items: center;
        width: 27px;
        height: 16px;

        & /deep/ svg {
            height: 20px;
        }
    }
}
</style>
